import CTAButton from '@/components/common/CTAButton';
import { useLanguage } from '@/contexts/LanguageContext';
import Icon from '@/components/ui/AppIcon';
import Image from 'next/image';

interface HeroSectionProps {
  headline: string;
  subheadline: string;
  quote: string;
  quoteAuthor: string;
}

const HeroSection = ({ headline, subheadline, quote, quoteAuthor }: HeroSectionProps) => {
  const { t } = useLanguage();

  return (
    <section className="relative bg-gradient-luxury text-primary-foreground overflow-hidden">
      {/* Realistic Gold Bars Background Decoration - Original Size */}
      <div className="absolute inset-0 pointer-events-none z-0">
        {/* Gold Bar 1 - Visible on all devices */}
        <div className="absolute top-10 left-10 transform -rotate-12 opacity-60">
          <Image
            src="/assets/images/pngegg-1769618935851.png"
            alt="Decorative gold bar"
            width={300}
            height={200}
            className="object-contain"
            style={{ filter: 'brightness(1.1)', width: 'auto', height: 'auto', maxWidth: '300px' }}
          />
        </div>
        
        {/* Gold Bar 2 - Hidden on mobile */}
        <div className="hidden md:block absolute top-40 right-20 transform rotate-6 opacity-60">
          <Image
            src="/assets/images/pngegg-1769618935851.png"
            alt="Decorative gold bar"
            width={300}
            height={200}
            className="object-contain"
            style={{ filter: 'brightness(1.1)', width: 'auto', height: 'auto', maxWidth: '300px' }}
          />
        </div>
        
        {/* Gold Bar 5 - Hidden on mobile */}
        <div className="hidden md:block absolute top-1/2 left-10 transform rotate-45 opacity-60">
          <Image
            src="/assets/images/pngegg-1769618935851.png"
            alt="Decorative gold bar"
            width={300}
            height={200}
            className="object-contain"
            style={{ filter: 'brightness(1.1)', width: 'auto', height: 'auto', maxWidth: '300px' }}
          />
        </div>
        
        {/* Gold Bar 6 - Visible on all devices */}
        <div className="absolute top-1/3 right-10 transform -rotate-45 opacity-60">
          <Image
            src="/assets/images/pngegg-1769618935851.png"
            alt="Decorative gold bar"
            width={300}
            height={200}
            className="object-contain"
            style={{ filter: 'brightness(1.1)', width: 'auto', height: 'auto', maxWidth: '300px' }}
          />
        </div>
      </div>
      
      <div className="relative mx-auto max-w-content px-6 lg:px-8 py-24 lg:py-40 z-10">
        <div className="max-w-5xl mx-auto text-center">
          {/* Main Headline */}
          <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-bold mb-8 leading-[1.1] tracking-tight">
            {headline}
          </h1>

          {/* Subheadline */}
          <p className="font-body text-xl md:text-2xl lg:text-3xl mb-16 text-primary-foreground/90 leading-relaxed font-light max-w-4xl mx-auto">
            {subheadline}
          </p>

          {/* Quote Box */}
          <div className="bg-white/5 backdrop-blur-md border border-accent/20 rounded-2xl p-10 lg:p-12 mb-16 max-w-3xl mx-auto shadow-luxury-lg hover:shadow-gold-glow transition-luxury">
            <blockquote className="relative">
              <div className="absolute -top-6 -left-6 w-12 h-12 bg-accent/20 rounded-full flex items-center justify-center">
                <Icon name="ChatBubbleLeftIcon" size={24} className="text-accent" />
              </div>
              <p className="font-accent text-xl md:text-2xl italic text-primary-foreground/95 mb-6 leading-relaxed">
                "{quote}"
              </p>
              <footer className="font-body text-base text-accent font-semibold tracking-wide">
                — {quoteAuthor}
              </footer>
            </blockquote>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-5 justify-center items-center">
            <CTAButton
              href="/contact"
              variant="primary"
              className="w-full sm:w-auto px-8 py-4 text-lg font-semibold shadow-luxury-md hover:shadow-luxury-lg">
              {t('home.cta.button')}
            </CTAButton>
          </div>
        </div>
      </div>
    </section>);

};

export default HeroSection;